import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  entityService,
  datasetService,
  datasetGroupService,
  datasourceService,
  attributeService,
  glossaryService,
  systemService,
  userService,
  userGroupService,
  userGroupMemberService,
} from '../services';
import {
  Asset,
  formatData,
  formatDatasetPath,
  handleError,
  Operation,
  Role,
  authMethod,
  resolveGlossaryAssetSubmit,
} from '../helpers';
import {
  Changelog,
  DataTable,
  MainColumn,
  MorphButton,
  ObjectDetails,
  SkeletonLoader,
  Tabs,
} from '../components';
import {
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  useSearch,
  useNotFound,
  useMessage,
} from '../hooks';

export const UserGroup = () => {
  const { queryParams, resetParams } = useQueryParams();

  const [userGroup, setUserGroup] = useState([]);
  const [Users, setUsers] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [ownedItems, setOwnedItems] = useState([]);
  const [ownedItemsCount, setOwnedItemsCount] = useState({});
  const [ownedItemsTotalCount, setOwnedItemsTotalCount] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingMoreOptions, setLoadingMoreOptions] = useState(false);
  const [defaultSelectedObject, setDefaultSelectedObject] = useState({ tab: queryParams.object });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { notFound, setNotFound, NotFound } = useNotFound();

  const {
    searchTerm,
    searchResults,
    searchResultsCount,
    setSearchTerm,
    fetchSearchResults,
    clearSearchResults,
    loadNextPage,
  } = useSearch({ setLoading: setLoadingMoreOptions, objectName: 'user', pagination: true });

  const { user_group_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchItems();
    fetchOwnedItemsCount();
    fetchMembers();
  }, [user_group_id]);

  const fetchItems = async () => {
    userGroupService
      .getById(user_group_id)
      .then(res => {
        if (res.user_groups.length < 1) {
          setNotFound(true);
        } else {
          setUserGroup(res.user_groups[0]);
          setLoading(false);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  const fetchOwnedItemsCount = async () => {
    userGroupService
      .getOwnedItemsCount(user_group_id)
      .then(res => {
        let tmp = {};
        let defaultObject;

        const objectSortOrder = [
          'attribute',
          'entity',
          'glossary',
          'dataset',
          'dataset_group',
          'datasource',
          'system',
        ];
        res.owned_items_count.sort(
          (a, b) => objectSortOrder.indexOf(a.object_name) - objectSortOrder.indexOf(b.object_name)
        );

        res.owned_items_count.forEach(result => {
          if (result.owned_items_count > 0 && defaultObject === undefined) {
            defaultObject = result.object_name;
          }
          tmp = { ...tmp, [result.object_name]: parseInt(result.owned_items_count) };
        });

        defaultObject = res.owned_items_count.find(x => x.object_name === queryParams.object)
          ? queryParams.object
          : defaultObject;

        //defaultObject = queryParams.object || defaultObject

        setDefaultSelectedObject({ tab: defaultObject });
        fetchOwnedItems(defaultObject);
        setOwnedItemsCount(tmp);

        setOwnedItemsTotalCount(
          (tmp.attribute || 0) +
            (tmp.dataset || 0) +
            (tmp.dataset_group || 0) +
            (tmp.datasource || 0) +
            (tmp.system || 0) +
            (tmp.entity || 0) +
            (tmp.glossary || 0)
        );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchOwnedItems = async objectName => {
    if (objectName) {
      // objectName is undefined if user does not own any items
      setLoadingTable(true);
      userGroupService
        .getOwnedItems(user_group_id, objectName)
        .then(res => {
          let result = res.owned_items;

          if (objectName === Asset.Dataset) {
            // Render each owned dataset as its full endpoint path (API datasets); the ancestors
            // are attached to the row by the owned-items endpoint.
            result = result.map(x => ({
              ...x,
              dataset_path: formatDatasetPath(x, { ancestors: x.ancestors }),
            }));
          }

          setOwnedItems(result);
          setLoadingTable(false);
        })
        .catch(err => {
          showMessage(err, 'error');
        });
    } else {
      setLoadingTable(false); // Needed to show "no result" message
    }
  };

  const fetchMembers = async () => {
    userGroupService
      .getMembers(user_group_id)
      .then(res => {
        setUsers(res.users);
        setLoadingMembers(false);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const updateMember = async event => {
    let { value } = '';

    value = event.value;
    const userGroupId = parseInt(user_group_id);

    const existingUser = Users.find(x => x.user_id === value);
    const user_group_members = [
      {
        user_id: value,
        user_group_id: userGroupId,
        user_group_member_id: existingUser?.user_group_member_id,
      },
    ];

    if (existingUser) {
      await removeMember(user_group_members);
    } else {
      await addMember(user_group_members);
    }
  };
  const addMember = async data => {
    //let { name, value } = ""

    //name = event.name
    //value = event.value
    //const userGroupId = parseInt(user_group_id)

    //const user_group_members = { user_group_members: [{ user_id: value, user_group_id: userGroupId }] }

    await userGroupMemberService
      .create({ user_group_members: data })
      .then(async res => {
        fetchSearchResults(undefined, false);
        fetchMembers();

        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editUserGroup = async data => {
    const userGroups = { user_groups: Array.isArray(data) ? data : [data] };

    await userGroupService
      .update(userGroups)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editUser = async data => {
    const users = { users: Array.isArray(data) ? data : [data] };

    await userService
      .update(users)
      .then(async res => {
        fetchMembers();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDataset = async data => {
    const items = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDatasetGroup = async data => {
    const items = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDatasource = async data => {
    const items = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editSystem = async data => {
    const items = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editGlossary = async data => {
    const items = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  /**
   * Saves an edited glossary asset. One handler serves both kinds: the form's role picker can move an
   * asset between Entity and Attribute, so which resource to call is decided from the submitted row
   * rather than from the button that opened the dialog.
   */
  const editGlossaryAsset = async data => {
    const { service, payload, isCreate } = resolveGlossaryAssetSubmit(data, 'edit');

    await (isCreate ? service.create(payload) : service.update(payload))
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteEntity = async data => {
    await entityService
      .destroy({ entities: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDataset = async data => {
    await datasetService
      .destroy({ datasets: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasetGroup = async data => {
    await datasetGroupService
      .destroy({ dataset_groups: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasource = async data => {
    await datasourceService
      .destroy({ datasources: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteAttribute = async data => {
    await attributeService
      .destroy({ attributes: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteGlossary = async data => {
    await glossaryService
      .destroy({ glossaries: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteSystem = async data => {
    await systemService
      .destroy({ systems: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        clearSelectedItems();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteUserGroup = async data => {
    await userGroupService
      .destroy({ user_groups: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/user_groups`);
        }
      })
      .catch(err => {
        showMessage(JSON.stringify(err), 'error');
      });
  };

  const removeMember = async data => {
    await userGroupMemberService
      .destroy({ user_group_members: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchMembers();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(JSON.stringify(err), 'error');
      });
  };

  const handleInputChange = value => {
    setSearchTerm(value);

    if (value.length > 1) {
      // Only trigger search query for search terms with more than 1 character
      fetchSearchResults(value, false, 1);
    }
  };

  const loadMoreOptions = () => {
    loadNextPage();
  };

  const onObjectChange = object => {
    resetParams({ object: object });
    ownedItems.length > 0 && fetchOwnedItems(object);
    setDefaultSelectedObject({ tab: object });
    clearSelectedItems();
  };

  const requestResults = searchResults.map(result => {
    return {
      value: result.searchable.user_id,
      label: result.searchable.user_fullname,
      secondaryLabel: result.searchable.user_username,
      photo: result.searchable.user_photo,
      tooltip: `${result.searchable.user_fullname} (${result.searchable.user_username})\n${result.searchable.user_title ? result.searchable.user_title : ''}`,
    };
  });

  const data = loading ? [] : userGroup;

  const editableAdmin = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const editable = editableAdmin;

  const userAuthMethodValues = Object.entries(authMethod).reduce(
    (a, [key, value]) => ({ ...a, [key]: { label: value } }),
    {}
  );
  const userIsEnabledValues = {
    true: { label: 'Enabled', className: 'pill' },
    false: { label: 'Disabled', className: 'pill pill-semifull-red' },
  };

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="USER_GROUP"
          title={data.user_group_name}
          description={data.user_group_description}
          details={[
            editable && { title: 'Source Id', data: data.user_group_source_id },
            editable && {
              title: data?.user_group_auth_method === 'local' ? 'Created' : 'Added',
              data: formatData(data.created_timestamp, 'datetime'),
            },
            {
              title: 'Id',
              data: data.user_group_id,
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          photo={data.user_group_photo}
          loading={loading}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editUserGroup', [data], editUserGroup),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteUserGroup', [data], deleteUserGroup),
            },
          ]}
        >
          <h3>{!loading ? 'Email' : <SkeletonLoader width="5vw" />}</h3>
          <div className="display-linebreak">
            {!loading ? (
              data.user_group_email === null ? (
                '-'
              ) : (
                <a className="link" href={'mailto:' + data.user_group_email}>
                  {data.user_group_email}
                </a>
              )
            ) : (
              <SkeletonLoader width="10vw" />
            )}
          </div>

          {editableAdmin && (
            <React.Fragment>
              <h3>{!loading ? 'User Provisioning' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  data.user_provisioning_is_enabled ? (
                    <span className="pill pill-black">Enabled</span>
                  ) : (
                    <span className="pill pill-grey">Disabled</span>
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>

              {data.user_provisioning_is_enabled && (
                <React.Fragment>
                  <h3>{!loading ? 'Default User Role' : <SkeletonLoader width="5vw" />}</h3>
                  <div className="display-linebreak">
                    {!loading ? (
                      data.user_group_default_role_name === null ? (
                        '-'
                      ) : (
                        data.user_group_default_role_name
                      )
                    ) : (
                      <SkeletonLoader width="10vw" />
                    )}
                  </div>
                </React.Fragment>
              )}

              <h3>{!loading ? 'User Group Type' : <SkeletonLoader width="5vw" />}</h3>
              <div className="display-linebreak">
                {!loading ? (
                  data.user_group_type === null ? (
                    '-'
                  ) : (
                    authMethod[data.user_group_type]
                  )
                ) : (
                  <SkeletonLoader width="10vw" />
                )}
              </div>
            </React.Fragment>
          )}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs className="slim left" disableTabsWithoutResults={true}>
          <div
            label="Members"
            tabid="members"
            resultCount={loadingMembers || Users.length === 0 ? undefined : Users.length}
          >
            <DataTable
              columns={[
                {
                  id: 'user_fullname',
                  name: '',
                  type: 'owner',
                  link: '/users/:user_id',
                  data: { photo: 'user_photo' },
                },
                { id: 'user_fullname', name: 'Name', link: '/users/:user_id', className: 'bold' },
                { id: 'user_username', className: 'small-text', name: 'Username' },
                ...(editableAdmin
                  ? [
                      { id: 'user_role_name', name: 'Role' },
                      {
                        id: 'user_auth_method',
                        name: 'Type',
                        value: 'user_auth_method',
                        values: userAuthMethodValues,
                        className: 'small-text',
                      },
                      {
                        id: 'user_is_enabled',
                        name: 'Status',
                        type: 'boolean',
                        values: userIsEnabledValues,
                      },
                    ]
                  : []),
              ]}
              buttons={[
                {
                  customButton: (
                    <MorphButton
                      name="user_id"
                      value={Users.map(x => ({ value: x.user_id, label: x.user_fullname }))}
                      options={requestResults}
                      onChange={updateMember}
                      placeholder="Search Users to add..."
                      onInputChange={handleInputChange}
                      loadMoreOptions={loadMoreOptions}
                      loading={loadingMoreOptions}
                      resultCount={searchResultsCount && searchResultsCount.user}
                      searchTerm={searchTerm}
                      clearSearchResults={clearSearchResults}
                      onToggle={clearSelectedItems}
                    />
                  ),
                  action: 'add',
                  onClick: () => {},
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items => showSideDialog('editUser', items || selectedItems, editUser),
                },
                {
                  label: 'Remove',
                  action: 'remove',
                  tooltip: 'Remove user from this user group. The User itself will not be deleted.',
                  onClick: items =>
                    showModalDialog('removeMember', items || selectedItems, removeMember),
                },
              ]}
              data={Users}
              idColumn="user_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingMembers}
              editable={editable}
              filterable={true}
              filterObjects={[
                'user_fullname',
                'user_username',
                ...(editableAdmin ? ['user_email', 'user_role_name'] : []),
              ]}
            />
          </div>

          <div label="Assigned Assets" tabid="owned_assets" resultCount={ownedItemsTotalCount}>
            {ownedItemsTotalCount > 0 ? (
              <Tabs
                className="buttons left"
                onTabChange={onObjectChange}
                showResultCount={false}
                disableTabsWithoutResults={true}
                defaultSelectedTab={defaultSelectedObject}
              >
                <div
                  label="Attributes"
                  tabid={Asset.Attribute}
                  resultCount={(ownedItemsCount && ownedItemsCount.attribute) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'attribute_role_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'attribute_role_name',
                      },
                      {
                        id: 'attribute_name',
                        name: 'Name',
                        link: '/attributes/:attribute_id',
                        tooltip: 'attribute_description',
                        className: 'bold',
                      },
                      {
                        id: 'attribute_unit_code',
                        name: 'Attribute Unit',
                        tooltip: 'attribute_unit_name',
                      },
                      {
                        id: 'attribute_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'modified_timestamp',
                        name: 'Modified',
                        type: 'datetime',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editAttribute',
                            items || selectedItems,
                            editGlossaryAsset
                          ),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteAttribute',
                            items || selectedItems,
                            deleteAttribute
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="attribute_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'attribute_name',
                      'attribute_description',
                      'attribute_role_name',
                      'attribute_unit_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Entities"
                  tabid={Asset.Entity}
                  resultCount={(ownedItemsCount && ownedItemsCount.entity) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'entity_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id/:entity_id',
                        tooltip: 'entity_description',
                        className: 'bold',
                      },
                      {
                        id: 'entity_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'glossary_name', name: 'Glossary', link: '/glossaries/:glossary_id' },
                      {
                        id: 'modified_timestamp',
                        name: 'Modified',
                        type: 'datetime',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editEntity', items || selectedItems, editGlossaryAsset),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteEntity', items || selectedItems, deleteEntity),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="entity_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'entity_name',
                      'entity_description',
                      'glossary_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Glossaries"
                  tabid={Asset.Glossary}
                  resultCount={(ownedItemsCount && ownedItemsCount.glossary) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'glossary_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id',
                        tooltip: 'glossary_description',
                        className: 'bold',
                      },
                      {
                        id: 'glossary_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'modified_timestamp',
                        name: 'Modified',
                        type: 'datetime',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editGlossary', items || selectedItems, editGlossary),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteGlossary', items || selectedItems, deleteGlossary),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="glossary_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={['glossary_name', 'glossary_description', 'assigned_as']}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasets"
                  tabid={Asset.Dataset}
                  resultCount={(ownedItemsCount && ownedItemsCount.dataset) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'dataset_type_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'dataset_type_name',
                      },
                      {
                        // dataset_path holds the formatDatasetPath() output (its segments link
                        // themselves); sort still keys off dataset_name.
                        id: 'dataset_path',
                        name: 'Name',
                        sortField: 'dataset_name',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                      },
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDataset', items || selectedItems, editDataset),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteDataset', items || selectedItems, deleteDataset),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="dataset_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'dataset_name',
                      'dataset_description',
                      'dataset_type_name',
                      'dataset_group_name',
                      'datasource_name',
                      'system_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Dataset Groups"
                  tabid={Asset.DatasetGroup}
                  resultCount={(ownedItemsCount && ownedItemsCount.dataset_group) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'dataset_group_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                        tooltip: 'dataset_group_description',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editDatasetGroup',
                            items || selectedItems,
                            editDatasetGroup
                          ),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteDatasetGroup',
                            items || selectedItems,
                            deleteDatasetGroup
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="dataset_group_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'dataset_group_name',
                      'dataset_description',
                      'datasource_name',
                      'system_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasources"
                  tabid={Asset.Datasource}
                  resultCount={(ownedItemsCount && ownedItemsCount.datasource) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id',
                        tooltip: 'datasource_description',
                        className: 'bold',
                      },
                      {
                        id: 'datasource_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'system_name', name: 'System', link: '/systems/:system_id' },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDatasource', items || selectedItems, editDatasource),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteDatasource',
                            items || selectedItems,
                            deleteDatasource
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="datasource_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'datasource_name',
                      'datasource_description',
                      'datasource_type_category',
                      'datasource_type_name',
                      'system_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Systems"
                  tabid={Asset.System}
                  resultCount={(ownedItemsCount && ownedItemsCount.system) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'system_name',
                        name: 'Name',
                        link: '/systems/:system_id',
                        tooltip: 'system_description',
                        className: 'bold',
                      },
                      { id: 'system_code', name: 'Code' },
                      {
                        id: 'system_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editSystem', items || selectedItems, editSystem),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteSystem', items || selectedItems, deleteSystem),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="system_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={['system_name', 'system_description', 'assigned_as']}
                    showViewModes={true}
                  />
                </div>
              </Tabs>
            ) : (
              !loadingTable && <div className="no-result-text">No assigned assets</div>
            )}
          </div>

          {editableAdmin && (
            <div label="Changelog" tabid="changelog">
              <Changelog
                objectName={Asset.UserGroup}
                objectId={user_group_id}
                filterObjects={[
                  'changedData.new_data||old_data.user_group_name',
                  'changedData.new_data||old_data.user_group_email',
                  'users.user_fullname',
                  'users.user_username',
                  'changedData.property',
                  'changed_by_user_username',
                  'is_service_account_user',
                ]}
                filterObjectsValueMap={{
                  is_service_account_user: {
                    true: { label: 'Datasource Sync' },
                    false: { label: '' },
                  },
                  operation: Operation,
                }}
              />
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
};
